import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'

import TicketSearchInput from '../../components/TicketSearchInput'
import { AGENT_TICKETS_QUERY } from '../../graphql/tickets'
import { listLoadErrorMessage } from '../../helpers/listLoadError'
import { validateAgentTicketsResponse } from '../../helpers/ticketResponseValidation'
import { Ticket, TicketList } from '../../helpers/ticketWorkflow'
import { useCursorPage } from '../../helpers/useCursorPage'
import { useDebouncedValue } from '../../helpers/useDebouncedValue'
import { getTranslate, lang } from '../../localization'
import SupportQueueRowMobile from './SupportQueueRowMobile'
import SupportQueueTable from './SupportQueueTable'
import useStyles from './styles/SupportQueueTable.styles'

/**
 * The cross-tenant L1 support queue (ADR-035 KDD-2).
 *
 * Rows link to the ORDINARY ticket detail screen, not a parallel agent one.
 * That works because ticket-service reads the request's tenant scope in exactly
 * one place — creating a ticket — while every read and mutate-by-id path gates
 * on the visibility predicate instead, which an admin passes for any tenant.
 * A second detail stack would have duplicated the transition, escalation and
 * conversation UI for no behavioural difference.
 *
 * The route's role check is an affordance. The real gate is server-side and
 * threefold: a Layer-1 permission on the operation name, the BFF re-checking
 * the verified is_admin claim, and ticket-service re-deriving the admin tier
 * from the JWT rather than trusting the BFF's routing flag.
 */
const PAGE_SIZE = 20

/**
 * Upper bound on rows kept in memory — about 25 "Load more" clicks. Generous
 * enough that normal triage never reaches it, low enough that a session left
 * open for a shift cannot degrade the screen.
 */
const MAX_RETAINED_ROWS = 500

export default function SupportAgentQueue() {
  const classes = useStyles()
  const history = useHistory()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [query, setQuery] = useState('')
  const search = useDebouncedValue(query)

  const {
    items: tickets,
    loading,
    error,
    validationError,
    hasMore,
    loadMore,
    refetch
  } = useCursorPage<{ supportAgentTickets: TicketList }, Ticket>({
    query: AGENT_TICKETS_QUERY,
    pageSize: PAGE_SIZE,
    variables: { search },
    // Unlike a tenant's own list, this queue is bounded by the whole platform's
    // ticket volume, so the accumulated array has no natural ceiling — and
    // every retained row re-renders on each page load, so cost grows faster
    // than the count. A triage view is a live queue, not an archive, so the
    // oldest rows are the right thing to drop.
    maxRetained: MAX_RETAINED_ROWS,
    getPage: (data) =>
      data && {
        items: data.supportAgentTickets.tickets,
        nextPageToken: data.supportAgentTickets.nextPageToken
      },
    withItems: (data, items) => ({
      supportAgentTickets: { ...data.supportAgentTickets, tickets: items }
    }),
    validate: validateAgentTicketsResponse
  })

  // Gates the empty state: nothing in flight and nothing failed, so empty
  // means empty rather than not-yet-arrived.
  // `!hasMore` matters: a first page that came back empty while a next-page
  // token exists is INCOMPLETE, not empty. Without it the list would show
  // "no data" over a query that is still walking the cursor.
  const settled = !loading && !error && !validationError && !hasMore

  // Server-side now, which matters most on this list: it retains at most
  // MAX_RETAINED_ROWS and drops the oldest, so a client-side filter could never
  // reach an older ticket however long the agent scrolled. The query can.
  const visible = tickets
  const filtering = Boolean(search.trim())
  const emptyMessage = getTranslate(
    filtering ? 'No tickets match this search.' : 'The queue is empty.'
  )

  return (
    <div>
      {/* No PageHeader and no repeated heading: this is a TAB now, and
          SupportTabs owns both the document title and the section title. Two
          PageHeaders mounted together fight over document.title (the parent's
          effect runs last and wins), and the tab already says "Support queue"
          directly above this. The subtitle stays — it explains what the list
          contains, which the tab label cannot. */}
      <Typography variant="body2" color="textSecondary">
        {getTranslate(
          'Tickets across customer entities. Rows needing Adotel are highlighted.'
        )}
      </Typography>

      <TicketSearchInput value={query} onChange={setQuery} />

      {(error || validationError) && (
        <Alert
          severity="error"
          style={{ marginTop: 16 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch?.()}>
              {getTranslate('Retry')}
            </Button>
          }>
          {listLoadErrorMessage(
            error,
            validationError,
            'Could not load the queue.'
          )}
        </Alert>
      )}

      {/* Separate desktop and mobile renderings behind <Hidden> (§2). The
          queue had NO mobile treatment at all: a six-column table with no
          scroll container, which on a phone either forces horizontal page
          scroll or squeezes every column. */}
      {!isMobile ? (
        <SupportQueueTable
          tickets={visible}
          loading={loading}
          settled={settled}
          emptyMessage={emptyMessage}
          reloadData={() => refetch?.()}
        />
      ) : (
        <div className={classes.mobileList}>
          {visible.map((ticket: Ticket) => (
            <SupportQueueRowMobile
              key={ticket.id}
              ticket={ticket}
              onClick={() =>
                history.push(`/${lang}/support/tickets/detail/${ticket.id}`)
              }
            />
          ))}
          {settled && !visible.length && (
            <Typography variant="body2" className={classes.mobileEmpty}>
              {emptyMessage}
            </Typography>
          )}
        </div>
      )}

      {hasMore && (
        <div className={classes.loadMoreContainer}>
          <Button variant="outlined" disabled={loading} onClick={loadMore}>
            {getTranslate('Load more')}
          </Button>
        </div>
      )}
    </div>
  )
}
